import { useRef, useState, type ChangeEvent } from 'react'
import { logError } from '@/utils/errorUtils'
import { notifications } from '@mantine/notifications'
import type { MedplumClient } from '@medplum/core'
import type { Coverage, DocumentReference } from '@medplum/fhirtypes'
import { ACCEPTED_IMAGE_TYPES, INSURANCE_CARD_SIDES } from '@/constants/insuranceConstants'
import type { CoverageTemplate, InsuranceCardSide, InsuranceRank } from '@/types/insuranceTypes'
import {
  extractCoverageInsuranceCardReferences,
  getNextInsuranceCardCaptureSide,
  getResourceIdFromReference,
  updateInsuranceCardPreview,
} from '@/utils/insuranceCardUtils'
import { getDocumentAttachmentUrl } from '@/utils/documentReferenceUtils'

export function useInsuranceCardState() {
  const [frontImageFile, setFrontImageFile] = useState<File | null>(null)
  const [backImageFile, setBackImageFile] = useState<File | null>(null)
  const [frontPreviewUrl, setFrontPreviewUrl] = useState<string | null>(null)
  const [backPreviewUrl, setBackPreviewUrl] = useState<string | null>(null)
  const [frontHasPersistedCard, setFrontHasPersistedCard] = useState(false)
  const [backHasPersistedCard, setBackHasPersistedCard] = useState(false)
  const [zoomedImageUrl, setZoomedImageUrl] = useState<string | null>(null)
  const frontInputRef = useRef<HTMLInputElement | null>(null)
  const backInputRef = useRef<HTMLInputElement | null>(null)

  const updatePreviewForSide = (side: InsuranceCardSide, source: File | string | null) => {
    const setPreview = side === 'front' ? setFrontPreviewUrl : setBackPreviewUrl
    updateInsuranceCardPreview(setPreview, source)
  }

  const resetInsuranceUploads = () => {
    setFrontImageFile(null)
    setBackImageFile(null)
    updatePreviewForSide('front', null)
    updatePreviewForSide('back', null)
    setFrontHasPersistedCard(false)
    setBackHasPersistedCard(false)
  }

  const clearPersistedCard = (side: InsuranceCardSide) => {
    const setHasPersistedCard =
      side === 'front' ? setFrontHasPersistedCard : setBackHasPersistedCard
    setHasPersistedCard(false)
  }

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  const base64ToFile = (base64: string, filename: string): File => {
    const arr = base64.split(',')
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg'
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    return new File([u8arr], filename, { type: mime })
  }

  const populateFromDraft = (template: CoverageTemplate) => {
    if (template.frontImageBase64) {
      setFrontPreviewUrl(template.frontImageBase64)
      setFrontImageFile(base64ToFile(template.frontImageBase64, 'front-insurance-card.jpg'))
    }

    if (template.backImageBase64) {
      setBackPreviewUrl(template.backImageBase64)
      setBackImageFile(base64ToFile(template.backImageBase64, 'back-insurance-card.jpg'))
    }
  }

  const validateCardSelection = (existingFront?: string, existingBack?: string): boolean => {
    const hasFront = Boolean(frontImageFile || existingFront)
    const hasBack = Boolean(backImageFile || existingBack)

    if (hasBack && !hasFront) {
      notifications.show({
        title: 'Front of card required',
        message: 'Add the front of the insurance card before saving.',
        color: 'red',
      })
      return false
    }

    return true
  }

  const handleInsuranceFileChange = (side: InsuranceCardSide, file: File | null): boolean => {
    if (!file) {
      if (side === 'front') {
        setFrontImageFile(null)
      } else {
        setBackImageFile(null)
      }
      updatePreviewForSide(side, null)
      return false
    }

    const normalizedType = (file.type || '').toLowerCase()
    const isAccepted = ACCEPTED_IMAGE_TYPES.includes(
      normalizedType as (typeof ACCEPTED_IMAGE_TYPES)[number],
    )
    if (!isAccepted) {
      notifications.show({
        title: 'Unsupported file type',
        message: 'Please upload JPG or PNG images.',
        color: 'red',
      })
      return false
    }

    if (side === 'front') {
      setFrontImageFile(file)
    } else {
      setBackImageFile(file)
    }
    updatePreviewForSide(side, file)
    return true
  }

  const getNextCaptureSide = (): InsuranceCardSide =>
    getNextInsuranceCardCaptureSide(
      frontImageFile || frontPreviewUrl,
      backImageFile || backPreviewUrl,
    )

  const handleStartInsuranceSelection = (activeInsuranceRank: InsuranceRank | null) => {
    if (!activeInsuranceRank) {
      notifications.show({
        title: 'Select insurance first',
        message: 'Open a specific insurance to attach its card images.',
        color: 'red',
      })
      return
    }

    const nextSide = getNextCaptureSide()
    if (nextSide === 'front') {
      frontInputRef.current?.click()
    } else {
      backInputRef.current?.click()
    }

    if ((frontImageFile || frontPreviewUrl) && (backImageFile || backPreviewUrl)) {
      notifications.show({
        title: 'Replacing images',
        message:
          'Both sides are already selected. Choosing a new photo will replace the existing one.',
        color: 'gray',
      })
    }
  }

  const handleRetakeImage = (
    side: InsuranceCardSide,
    activeInsuranceRank: InsuranceRank | null,
  ) => {
    if (!activeInsuranceRank) {
      notifications.show({
        title: 'Select insurance first',
        message: 'Open the insurance you want to attach images to.',
        color: 'red',
      })
      return
    }

    if (side === 'front') {
      frontInputRef.current?.click()
    } else {
      backInputRef.current?.click()
    }
  }

  const handleFrontInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0] ?? null
    handleInsuranceFileChange('front', file)
    event.currentTarget.value = ''
  }

  const handleBackInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0] ?? null
    handleInsuranceFileChange('back', file)
    event.currentTarget.value = ''
  }

  const loadExistingCoverageImages = async (medplum: MedplumClient, coverage: Coverage) => {
    const references = extractCoverageInsuranceCardReferences(coverage)
    setFrontHasPersistedCard(Boolean(references.front))
    setBackHasPersistedCard(Boolean(references.back))

    const previewUrls: Partial<Record<InsuranceCardSide, string>> = {}

    await Promise.all(
      INSURANCE_CARD_SIDES.map(async (side) => {
        const reference = references[side]
        const documentId = getResourceIdFromReference(reference)
        if (!documentId) {
          return
        }

        try {
          const document = await medplum.readResource('DocumentReference', documentId)
          const url = getDocumentAttachmentUrl(document as DocumentReference)
          if (url) {
            previewUrls[side] = url
          }
        } catch (error) {
          logError('insurance', 'Failed to read insurance card document', error)
        }
      }),
    )

    if (!frontImageFile && previewUrls.front) {
      updatePreviewForSide('front', previewUrls.front)
    }

    if (!backImageFile && previewUrls.back) {
      updatePreviewForSide('back', previewUrls.back)
    }
  }

  return {
    backHasPersistedCard,
    backImageFile,
    backInputRef,
    backPreviewUrl,
    base64ToFile,
    clearPersistedCard,
    fileToBase64,
    frontHasPersistedCard,
    frontImageFile,
    frontInputRef,
    frontPreviewUrl,
    handleBackInputChange,
    handleFrontInputChange,
    handleInsuranceFileChange,
    handleRetakeImage,
    handleStartInsuranceSelection,
    hasAnyInsurancePreview: Boolean(frontPreviewUrl || backPreviewUrl),
    hasNewInsuranceCardSelection: Boolean(frontImageFile || backImageFile),
    loadExistingCoverageImages,
    populateFromDraft,
    resetInsuranceUploads,
    setZoomedImageUrl,
    updatePreviewForSide,
    validateCardSelection,
    zoomedImageUrl,
  }
}
